import {
    CSSProperties,
    ChangeEvent,
    FormEvent,
    MouseEvent,
    ReactNode,
    forwardRef,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { union } from 'lodash/fp';

import _ from '../locale';
import { styled } from '../theme';
import {
    AgeGroupEntity,
    CountryLocationSdi,
    DataKey,
    DataValue,
    Entity,
    SmartOptionConfig,
    SmartOptionResource,
} from '../types';
import { Color, Size, matchSize } from '../utility/common-props-helpers';
import Button from './Button';
import FlexRow from './FlexRow';
import FormikTextarea from './FormTextarea';
import H3 from './H3';
import Link from './Link';
import Modal from './Modal';
import ModalBody from './ModalBody';
import ModalButton from './ModalButton';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import { SmartOptionControl } from './index';

/**
 * Styled
 */

const Base = styled('div')<Pick<Props, 'size'>>(({ size }) => ({
    minHeight: matchSize({ md: 40, lg: 50 })(size),
    fontSize: matchSize({ md: '1.4rem', lg: '1.6rem' })(size),
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'start',
    paddingTop: 18,
}));

const CancelButton = styled(Link)(({ theme }) => ({
    color: theme.color.error,
}));

const StyledText = styled('span')(({ theme }) => ({
    color: theme.color.jetBlack,
}));

/**
 * Component
 */

type Props = {
    children?: ReactNode;
    className?: string;
    style?: CSSProperties;
    size?: Size;
    color?: Color;
    title?: string;
    value: DataValue;
    options: DataValue;
    granularityKey: DataKey;
    onChange: (items: DataValue) => void;
    ageGroupEntities: AgeGroupEntity[];
    countryLocationSdi: CountryLocationSdi[];
    entityHierarchies: Partial<Record<string, null | Entity[]>>;
    smartOptions: SmartOptionConfig[];
    onSmartOptionCreate?: (resource: SmartOptionResource) => void;
    onSmartOptionDelete?: (id: number) => void;
};

const toCsvText = (values: DataValue): string => values.join(', ');

const fromCsvText = (text: string): string[] =>
    text
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

const CsvImportModal = forwardRef<HTMLDivElement, Props>(
    (
        {
            children,
            size = 'md',
            color = 'base',
            title,
            onChange,
            value,
            options,
            ageGroupEntities,
            entityHierarchies,
            countryLocationSdi,
            smartOptions,
            granularityKey,
            onSmartOptionCreate,
            onSmartOptionDelete,
            ...props
        },
        ref
    ) => {
        const initialText = toCsvText(value);
        const [text, setText] = useState<string>(initialText);
        const [isVisible, setIsVisible] = useState(false);
        const parsedTextValues = useMemo(() => fromCsvText(text), [text]);

        useEffect(() => {
            setText(initialText);
        }, [initialText]);

        const parsedTextValuesLength = parsedTextValues.length;
        const isSaveDisabled = parsedTextValuesLength === 0 || text === initialText;
        const buttonText = parsedTextValuesLength ? (
            <StyledText>
                <b>{parsedTextValuesLength}</b> {_('ids')}
            </StyledText>
        ) : (
            _('add_ids')
        );

        const closeModal = () => {
            setIsVisible(false);
            setText(initialText);
        };
        const openModal = () => {
            setIsVisible(true);
        };

        const saveCsv = (ev: FormEvent<HTMLFormElement>) => {
            ev.preventDefault();
            closeModal();
            onChange(parsedTextValues);
        };

        const clearSelection = (ev: MouseEvent<HTMLElement>) => {
            ev.preventDefault();
            setText('');
            onChange([]);
        };

        const handleSmartOptionChange = (smartOptionValue: DataValue) => {
            onChange(union(value, smartOptionValue));
        };

        return (
            <Base size={size} ref={ref} {...props}>
                <FlexRow itemsSpacing={20} align="center">
                    <Button color={color} onClick={openModal}>
                        {buttonText}
                    </Button>
                    {/* @todo: move buttons out of modal */}
                    <SmartOptionControl
                        granularity={options}
                        onChange={handleSmartOptionChange}
                        smartOptions={smartOptions}
                        isCreatable
                        value={value}
                        granularityKey={granularityKey}
                        onCreate={onSmartOptionCreate}
                        onDelete={onSmartOptionDelete}
                    />
                    <CancelButton onClick={clearSelection}>{_('clear')}</CancelButton>
                </FlexRow>

                {/* @todo: replace with SubmitModal */}
                <Modal show={isVisible} onHide={closeModal} {...props}>
                    <form onSubmit={saveCsv}>
                        {title && (
                            <ModalHeader closeButton>
                                <H3>{title}</H3>
                            </ModalHeader>
                        )}
                        <ModalBody>
                            <FormikTextarea
                                name={_('comma_separated_ids')}
                                rows="5"
                                value={text}
                                onChange={(ev: ChangeEvent<HTMLTextAreaElement>) => {
                                    setText(ev.target.value);
                                }}
                            />
                        </ModalBody>
                        <ModalFooter>
                            <ModalButton color="secondary" onClick={closeModal} type="button">
                                {_('cancel_button')}
                            </ModalButton>
                            <ModalButton type="submit" disabled={isSaveDisabled}>
                                {_('save_button')}
                            </ModalButton>
                        </ModalFooter>
                    </form>
                </Modal>
            </Base>
        );
    }
);

export default CsvImportModal;
